import { CommonModule, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { IProductDataV2 } from '@df/catalog/product/product';
import { GtmListDirective } from '@df/module-gtm/integration/gtm-list.directive';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { SearchResult } from '@df/search/search-result';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { NToggle } from '@df/ui/toggle/toggle.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { IlinkListItem, LinkList1Component } from 'projects/app-default/src/app/common/link-list1/link-list1.component';
import { ButtonStyleComponent } from '../../../common/buttons/button-style/button-style.component';
import { InlineButtonComponent } from '../../../common/buttons/inline-button/inline-button.component';
import { NPageSearch } from '../page-search.interfaces';
import { PageSearchService } from '../page-search.service';
import { GridDirective } from '@df/ui/atomic/atom/grid/grid.directive';
import { GridColumnDirective } from '@df/ui/atomic/atom/grid/grid-column.directive';
import { GtmItemDirective } from '../../../../../../../git_modules/@tomandco/dragonfly-module-gtm/src/integration/gtm-item.directive';
import { ProductComponent } from '../../../catalog/product/product/product.component';
import { Product } from '../../../catalog/product/product.decorator';

@Component({
    imports: [
        CommonModule,
        LinkList1Component,
        SizeClassDirective,
        GridDirective,
        GridColumnDirective,
        ButtonStyleComponent,
        TranslationModule,
        GtmListDirective,
        ICmsClientModule,
        DfUiCarouselModule,
        RouterModule,
        DfUiModule,
        DfModuleImageModule,
        InlineButtonComponent,
        HrefComponent,
        GtmItemDirective,
        ProductComponent
    ],
    selector: 'page-search-outlet',
    templateUrl: 'page-search-outlet.html',
    styleUrl: 'page-search-outlet.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageSearchOutletComponent>({
    id: NPageSearch.OUTLET_BLOCK_ID,
    block: {
        url: NPageSearch.OUTLET_BLOCK_ID,
        scope: 'website',
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    name: 'Search outlet',
    description: 'Header search outlet content',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'feature-links',
            title: 'Feature Links'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'featureLinksSections',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Section',
            itemName: 'Section',
            itemNameField: 'title',
            fields: [
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    default: 'Lorm ipsum'
                },
                <NICmsContentField.IRepeaterField>{
                    key: 'links',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Links',
                    itemName: 'Link',
                    itemNameField: 'label',
                    fields: [
                        {
                            key: 'label',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Label',
                            default: 'Lorem',
                            required: true
                        },
                        {
                            key: 'link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Link',
                            default: '/'
                        },
                        {
                            key: 'image',
                            type: NICmsContentField.EFieldType.image,
                            title: 'Image'
                        }
                    ]
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'noResult',
            title: 'No result message'
        },
        {
            key: 'noResultTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            description: 'Use % to mark where users search term should be displayed ',
            default: 'Lorm ipsum'
        },
        {
            key: 'noResultSubtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle',
            description: 'Use % to mark where users search term should be displayed ',
            default: 'Lorm ipsum'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'trending',
            title: 'Content cards'
        },
        {
            key: 'contentCardsTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Trending'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'contentCards',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Cards',
            itemName: 'Item',
            itemNameField: 'title',
            fields: [
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    required: true
                },
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    default: 'Lorem ipsum dolor',
                    required: true
                },
                {
                    key: 'buttonLabel',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button label',
                    default: 'Find out more',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    default: '/',
                    required: true
                }
            ],
            defaultSize: 3,
            maxSize: 4
        }
    ]
})
export class PageSearchOutletComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent',
        watchDepth: 0
    })
    content?: NPageSearch.IBlockDataContent;

    /**
     * Currently visible search result
     */
    result?: SearchResult;

    private readonly pageSearchService = inject(PageSearchService);
    /**
     * ids for tab switching on mobile
     */
    private readonly tabGroup = 'pageSearchOutletTabs';
    readonly termsId: NToggle.TId = [this.tabGroup, 'terms'];
    readonly postsId: NToggle.TId = [this.tabGroup, 'posts'];

    /**
     * Initialization
     */
    override ngOnInit() {
        this.subscriptions.add(
            this.pageSearchService.state.subscribe(result => {
                if (this.result !== result) {
                    this.result = result;
                    this.detectChanges();
                }
            })
        );
        super.ngOnInit();
    }

    /**
     * Go to search view page
     */
    gotToSearchView() {
        this.pageSearchService.gotToSearchView(this.result?.term);
    }

    /**
     * If we do show search results (or init screen)
     */
    get isSearchResult(): boolean {
        return !!this.result;
    }

    /**
     * If suggested terms can be shown
     */
    get canShowTerms(): boolean {
        return !!(this.result?.terms && this.result?.terms.length);
    }

    /**
     * If side content from cms should be shown
     */
    get canShowCmsSideContent(): boolean {
        return !this.canShowProductResults || this.client.isX || this.client.isL;
    }

    /**
     * If product results can be shown
     */
    get canShowProductResults(): boolean {
        return !!this.result?.hasProducts;
    }

    /**
     * Search terms normalized to link list format
     */
    get termsAsLinks(): IlinkListItem[] {
        return this.result?.terms?.map(i => ({ label: i.phrase, link: i.redirect || `/search/${i.phrase}` })) || [];
    }

    get mobileTerms() {
        return this.termsAsLinks.map(term => ({
            label: term.label.replace(this.term, `<span class='col-1'>${this.term}</span>`),
            link: term.link
        }));
    }
    /**
     * Pages normalized to link list format
     */
    get pagesAsLinks(): IlinkListItem[] {
        return this.result?.pages?.map(i => ({ label: i.name, link: i.data?.url || '', image: (i.data as IPageDataV2).image })) || [];
    }

    /**
     * Search term
     */
    get term(): string {
        return this.result?.term || '';
    }

    /**
     * No results messaging
     */
    get noResultTitle(): string {
        if (this.content && this.content.noResultTitle) {
            return this.content.noResultTitle.replace('%', this.term);
        }
        return '';
    }

    get noResultSubtitle(): string {
        if (this.content && this.content.noResultSubtitle) {
            return this.content.noResultSubtitle.replace('%', this.term);
        }
        return '';
    }

    get resultProducts() {
        return this.result?.products?.slice(0, 4) as Product<IProductDataV2>[];
    }
}
